import { minValue, required } from "ra-core";
import { useWatch } from "react-hook-form";

import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { DateInput } from "@/components/admin/date-input";
import { FileInput } from "@/components/admin/file-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";

import { AttachmentField } from "../../notes/AttachmentField";
import {
  FINANCE_CHARGE_KINDS,
  paymentMethodChoices,
  toChoices,
} from "../financeOptions";

const kindChoices = toChoices(
  FINANCE_CHARGE_KINDS,
  "crm.finance.charges.kinds",
);

/** Contracts of the selected company only */
const ContractInput = () => {
  const companyId = useWatch({ name: "company_id" });
  return (
    <ReferenceInput
      source="contract_id"
      reference="finance_contracts"
      filter={companyId ? { company_id: companyId } : {}}
      sort={{ field: "name", order: "ASC" }}
    >
      <SelectInput
        label="crm.finance.charges.fields.contract"
        optionText="name"
        emptyText="crm.finance.charges.no_contract"
        helperText={false}
      />
    </ReferenceInput>
  );
};

/** NF and receipt uploads, shared by the charge and payment dialogs */
export const ChargeFileInputs = () => (
  <>
    <FileInput
      source="invoice_files"
      label="crm.finance.charges.fields.invoice_files"
      accept={{ "application/pdf": [], "text/xml": [], "application/xml": [] }}
      multiple
    >
      <AttachmentField source="src" title="title" target="_blank" />
    </FileInput>
    <FileInput
      source="receipt_files"
      label="crm.finance.charges.fields.receipt_files"
      multiple
    >
      <AttachmentField source="src" title="title" target="_blank" />
    </FileInput>
  </>
);

/** Fields of the receivable form */
export const ChargeInputs = () => (
  <div className="flex flex-col gap-4">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <ReferenceInput
        source="company_id"
        reference="companies"
        sort={{ field: "name", order: "ASC" }}
      >
        <AutocompleteInput
          label="crm.finance.fields.company"
          optionText="name"
          validate={required()}
          helperText={false}
          modal
        />
      </ReferenceInput>
      <ContractInput />
      <SelectInput
        source="kind"
        label="crm.finance.charges.fields.kind"
        choices={kindChoices}
        validate={required()}
        helperText={false}
      />
      <NumberInput
        source="amount"
        label="crm.finance.fields.amount"
        min={0}
        step={0.01}
        validate={[required(), minValue(0)]}
        helperText={false}
      />
    </div>
    <TextInput
      source="description"
      label="crm.finance.fields.description"
      helperText={false}
    />
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <DateInput
        source="competence_month"
        label="crm.finance.fields.competence_month"
        validate={required()}
        helperText="crm.finance.fields.competence_month_help"
      />
      <DateInput
        source="due_date"
        label="crm.finance.fields.due_date"
        validate={required()}
        helperText={false}
      />
      <DateInput
        source="sent_on"
        label="crm.finance.charges.fields.sent_at"
        helperText={false}
      />
      <DateInput
        source="paid_on"
        label="crm.finance.fields.paid_at"
        helperText={false}
      />
      <NumberInput
        source="paid_amount"
        label="crm.finance.charges.fields.paid_amount"
        min={0}
        step={0.01}
        helperText={false}
      />
      <SelectInput
        source="payment_method"
        label="crm.finance.charges.fields.payment_method"
        choices={paymentMethodChoices}
        emptyText="crm.finance.none"
        helperText={false}
      />
      <TextInput
        source="invoice_number"
        label="crm.finance.charges.fields.invoice_number"
        helperText={false}
      />
    </div>
    <ChargeFileInputs />
    <TextInput
      source="notes"
      label="crm.finance.fields.notes"
      multiline
      helperText={false}
    />
  </div>
);
